import type { DriverTaskKpiRow, SortDirection } from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DriverKpiSortColumn = 'assigned' | 'driver' | 'failed' | 'late' | 'ontime';

// ─── Constants ──────────────────────────────────────────────────────────────

const NUMERIC_SORT_FIELDS: Record<Exclude<DriverKpiSortColumn, 'driver'>, keyof DriverTaskKpiRow> = {
  assigned: 'assigned',
  failed: 'failed',
  late: 'late',
  ontime: 'ontime',
};

// ─── Public API ─────────────────────────────────────────────────────────────

/** Sorts `driver-task-kpis` rows client-side by the given column/direction — a no-op when either is absent. Ties keep their original order in both directions. */
export function sortDriverKpiRows(
  rows: DriverTaskKpiRow[],
  sortBy: DriverKpiSortColumn | undefined,
  sortDirection: SortDirection | undefined,
): DriverTaskKpiRow[] {
  if (!sortBy || !sortDirection) return rows;

  const comparator = sortBy === 'driver' ? compareByDriverName : buildNumericComparator(NUMERIC_SORT_FIELDS[sortBy]);
  const directionMultiplier = sortDirection === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => comparator(a, b) * directionMultiplier);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function compareByDriverName(a: DriverTaskKpiRow, b: DriverTaskKpiRow): number {
  return a.driverName.localeCompare(b.driverName);
}

function buildNumericComparator(field: keyof DriverTaskKpiRow): (a: DriverTaskKpiRow, b: DriverTaskKpiRow) => number {
  return (a, b): number => Number(a[field]) - Number(b[field]);
}
